import axios from 'axios';
import pinia from '@/stores';
import { hintReportError } from '@/scripts/QuasarNotify';
import * as Sentry from '@sentry/vue';
import { createApp } from 'vue';
import App from '@/App.vue';

const app = createApp(App);
app.use(pinia);

import { useLoginTimeout } from '@/stores/login-timeout.js';
import { useReportError } from '@/stores/report-error';
import { useUserStore } from '@/stores/UserStore';

const dialog = useLoginTimeout();
const reportError = useReportError();
const user = useUserStore();

import { version } from '@/../package.json';

const api = axios.create({
  baseURL: '/api',
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true
});

const expectedError = (error) => {
  const msg = [
    'EmployeeNotFound',
    'The employee has already left the company',
    'Invalid EmpId',
    'PermissionDenied'
  ];

  return msg.includes(error.response.data?.msg || error.message || false);
};

// 添加axios攔截error給Sentry
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // - 請求已經發出，得到非 2xx
    // > 如果error 符合 expectedError，則不顯示錯誤訊息
    if (!expectedError(error)) {
      if (error.request.status === 401) {
        if (error.config.url !== '/auth/login') {
          dialog.statusDialog(true);
        }
      } else {
        // > 忽略 401 錯誤，因為只代表user未登入
        hintReportError();

        const parsedError = `
          Version: ${version} \n
          EmpID: ${user.empId} \n
          Method: ${error.config.method} \n
          URL: ${error.request.responseURL} \n
          Payload: ${error.config.data} \n
          Error: ${JSON.stringify(error.response.data || error.message)}
        `;

        // 使用 Sentry.withScope 來確保上下文隔離
        Sentry.withScope((scope) => {
          // 設置用戶資訊和標籤
          scope.setUser({
            id: user.empId,
            empName: user.empName
          });
          scope.setTag('empId', user.empId);
          scope.setTag('empName', user.empName);
          scope.setTag('apiEndpoint', error.config.url);
          scope.setTag('httpMethod', error.config.method);
          scope.setTag('httpStatus', error.response?.status);

          // 設置上下文資訊
          scope.setContext('request', {
            url: error.request.responseURL,
            method: error.config.method,
            data: error.config.data,
            headers: error.config.headers
          });

          scope.setContext('response', {
            status: error.response?.status,
            data: error.response?.data,
            headers: error.response?.headers
          });

          // 添加詳細的 breadcrumb
          scope.addBreadcrumb({
            timestamp: new Date().toISOString(),
            category: 'http',
            level: 'error',
            message: `HTTP ${error.response?.status} - ${error.config.method?.toUpperCase()} ${error.config.url}`,
            data: {
              url: error.config.url,
              method: error.config.method,
              status: error.response?.status,
              responseData: error.response?.data
            }
          });

          // 捕獲異常
          scope.captureException(error);
        });

        reportError.errorMsg = parsedError;
      }
    }

    return Promise.reject(error.response.data || error.message);
  }
);

export default api;
